import { Checkbox, Icon, Spinner, Tooltip } from '@blueprintjs/core';
import styles from './Lender.module.css';
import { classNames } from '@axo/shared/util/dom';
import { MarketCountryCode } from '@axo/shared/data-access/types/ace_index';
import { AvailableLender } from '../IAvailableLenders';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationData/ApplicationDataForm/ApplicationDataFormSchema';
import { ChangeEventHandler, useCallback, useEffect } from 'react';
import { Bank } from '@axo/ui-content/features/Partners';
import type { BankCode } from '@axo/ui-content/features/Partners/Bank';
import { useLoanApplication } from '@axo/shared/data-access/hooks/queries/useLoanApplication';
import { Product } from '@axo/shared/data-access/types';

export interface ILenderSelect {
  applicationID: string;
  lender: AvailableLender;
  marketCountry: MarketCountryCode;
}

export const Lender: React.FC<ILenderSelect> = ({
  applicationID,
  lender,
  marketCountry,
}) => {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { watch, setValue } = useFormContext<ApplicationDataFormValues>();
  const selectedLenders = watch('SelectedLenders');

  const checked = selectedLenders.has(lender.lenderID);

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.target.checked) {
        selectedLenders.add(lender.lenderID);
      } else {
        selectedLenders.delete(lender.lenderID);
      }

      setValue('SelectedLenders', selectedLenders);
    },
    [lender.lenderID, selectedLenders, setValue],
  );

  useEffect(() => {
    if (lender.state.disabled && selectedLenders.has(lender.lenderID)) {
      selectedLenders.delete(lender.lenderID);
      setValue('SelectedLenders', selectedLenders);
    }
  }, [lender.lenderID, lender.state.disabled, selectedLenders, setValue]);

  const canOverrideCheckbox = applicationData?.Product === Product.SecuredLoan;

  return (
    <Tooltip
      content={
        <div>
          <div>{lender.name}</div>
          {lender.state.disabled && <div>{lender.state.reason}</div>}
        </div>
      }
    >
      <div
        className={classNames(
          styles.container,
          lender.state.disabled && styles.disabled,
        )}
      >
        <div className={styles.checkboxContainer}>
          <Checkbox
            className={styles.checkbox}
            disabled={
              (lender.state.disabled && !canOverrideCheckbox) ||
              lender.state.loading
            }
            checked={checked}
            onChange={handleOnChange}
          />
        </div>
        <div className={styles.logoContainer}>
          <Bank
            code={lender.shortName as BankCode}
            name={lender.name}
            country={marketCountry}
            className={styles.logo}
          />
        </div>

        <div className={styles.disableContainer}>
          {lender.state.loading && !lender.state.disabled && (
            <Spinner size={14} />
          )}
          {lender.state.disabled && (
            <Icon icon="disable" className={styles.diableIcon} size={12} />
          )}
        </div>
      </div>
    </Tooltip>
  );
};
